import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useToast } from '@centraoneplus/ui';
import { authService } from '@services/auth.service';

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { emailSent } = await authService.requestPasswordReset(email);

      if (emailSent) {
        toast.success('Reset link sent', 'Check your email for the password reset link.');
        setSubmitted(true);
      } else {
        // Server accepted the request but could not deliver the email
        // (e.g. notification service / SMTP failure).
        toast.error('Email could not be sent', 'Please try again or contact your administrator.');
        setError('We could not send the reset email. Please try again or contact your administrator.');
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ error: ApiError }>;

      if (axiosError.response?.status === 429) {
        setError('Too many requests. Please try again later.');
      } else if (axiosError.response?.data?.error?.message) {
        setError(axiosError.response.data.error.message);
      } else {
        setError('An error occurred. Please try again.');
      }
      toast.error('Request failed', 'Could not submit the password reset request.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Check your email
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
          If an account exists for <span className="font-medium text-gray-900 dark:text-white">{email}</span>, we've sent
          a password reset link. The link will expire in 1 hour.
        </p>
        <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:text-primary-500 font-medium">
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Forgot your password?</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Enter your email and we'll send you a link to reset it.
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="px-4 py-3 rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            placeholder="Enter your email"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending...' : 'Send reset link'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        Remembered your password?{' '}
        <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:text-primary-500 font-medium">
          Sign in
        </Link>
      </p>
    </div>
  );
}
